import { useState, useEffect } from "react";
import styled from "styled-components";
import moment from "moment";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Button, Snackbar, Alert } from "@mui/material";

interface ProposalFormContentProps {
  selectedItems: Array<{
    option_id: string;
    start_time_id: string;
    date: string;
    tour_group_id: string;
    product_id: string;
  }>;
  entities: any;
  onClose: (e: React.MouseEvent) => void;
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 400px;
  height: 100%;
  padding: 16px;
  background-color: #ffffff;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, sans-serif;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-size: 18px;
  color: #333;
  margin: 0;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TextAreaContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  min-height: 250px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  background-color: #fafafa;
  &:focus {
    outline: none;
    border-color: #9dd8ff;
    box-shadow: 0 0 0 2px rgba(157, 216, 255, 0.2);
  }
`;
const CopyButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const InfoBox = styled.div`
  padding: 12px;
  background-color: #f0f7ff;
  border-left: 4px solid #4361ee;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #333;
`;

const ProposalFormContent = ({
  selectedItems,
  entities,
  onClose,
}: ProposalFormContentProps) => {
  const [proposalText, setProposalText] = useState<string>("");
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  useEffect(() => {
    // Sort items by date and then by start time
    const sortedItems = [...selectedItems].sort((a, b) => {
      const dateCompare = moment(a.date).valueOf() - moment(b.date).valueOf();
      if (dateCompare !== 0) return dateCompare;

      const aStartTime =
        entities?.data?.products
          ?.find((p: any) => p._id === a.product_id)
          ?.start_times.find((st: any) => st._id === a.start_time_id)
          ?.time_slot || "";

      const bStartTime =
        entities?.data?.products
          ?.find((p: any) => p._id === b.product_id)
          ?.start_times.find((st: any) => st._id === b.start_time_id)
          ?.time_slot || "";

      return aStartTime.localeCompare(bStartTime);
    });

    // Format the text
    let formattedText = "";
    sortedItems.forEach((item, index) => {
      const product = entities?.data?.products?.find(
        (p: any) => p._id === item.product_id
      );
      const option = product?.options.find(
        (o: any) => o._id === item.option_id
      );
      const startTime = product?.start_times.find(
        (st: any) => st._id === item.start_time_id
      );

      const formattedDate = moment(item.date).format("ddd, DD MMM, YYYY");
      const timeSlot = startTime?.time_slot || "N/A";
      const identifier =
        option?.guide_assignment_identifier || product?.title || "N/A";

      formattedText += `${
        index + 1
      }. ${formattedDate} - ${timeSlot} - ${identifier}\n`;
    });

    setProposalText(formattedText);
  }, [selectedItems, entities]);

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(proposalText).then(
      () => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 3000);
      },
      () => {
        alert("Failed to copy text to clipboard");
      }
    );
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setProposalText(e.target.value);
  };

  return (
    <Container>
      <Header>
        <Title>Proposal Form</Title>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Header>

      <InfoBox>
        This proposal form contains a formatted list of your selected groups.
        You can edit the text if needed or copy it to share with others.
      </InfoBox>

      <Content>
        <TextAreaContainer>
          <CopyButtonContainer>
            <Button
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyToClipboard}
              variant="text"
              size="small"
            >
              Copy to clipboard
            </Button>
          </CopyButtonContainer>
          <StyledTextArea
            value={proposalText}
            onChange={handleTextChange}
            placeholder="No items selected..."
          />
        </TextAreaContainer>
      </Content>

      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" sx={{ width: "100%" }}>
          Copied to clipboard!
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProposalFormContent;
